import { renderXAxisLabels, renderYAxisLabels } from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { interpolateColors } from '../../helpers/colors';
import { getSelectionHighlightColor, getStandardDataColor } from '../../../themes/themes';
import { renderYLabel } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';

import { chartDims, generateChartTitle } from '../../helpers/misc';
import { getLabelSpaceSize } from '../../helpers/configParams';
import { PLACEHOLDER_OTHER, createContingencyTable } from '../../helpers/categoricalTools';
import { getDualCategoricalEnFilterStep } from '../../../../data/selection/selectionEnFilterDualCategorical';
import { currentTheme, getThemedGrayFraction } from '../../../themes/themes';

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;
  const dataCatsX = boundData.bindings.catsX.data;
  const dataCatsY = boundData.bindings.catsY.data;
  const colCatsX = boundData.bindings.catsX.columnInfo;
  const colCatsY = boundData.bindings.catsY.columnInfo;
  const dataItemId = boundData.bindings.itemId.data;
  const nameX = boundData.bindings.catsX.columnInfo.name;
  const nameY = boundData.bindings.catsY.columnInfo.name;
  const title = generateChartTitle(dataContext, dataBinding, config, `${nameX} x ${nameY}`);

  const itemCount = dataCatsX.length;

  const filterInfo = getDualCategoricalEnFilterStep(
    boundData.canonicalTypeInfo.selectionFilter,
    boundData.dataTable.id,
    colCatsX.id,
    colCatsY.id,
  );

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  const contingencyTable = createContingencyTable(
    boundData.canonicalTypeInfo,
    dataCatsX,
    colCatsX,
    config.sortByX,
    config.maxCatCountX,
    dataCatsY,
    colCatsY,
    config.sortByY,
    config.maxCatCountY,
    dataItemId,
  );

  const categoriesXData = contingencyTable.categoriesX.categories;
  const categoriesYData = contingencyTable.categoriesY.categories;
  const cells = contingencyTable.cells;
  const catCountX = categoriesXData.length;
  const catCountY = categoriesYData.length;
  const maxCellCount = contingencyTable.maxCellCount;

  const catXLabels = categoriesXData.map((cat) => cat.displayString);
  const catYLabels = categoriesYData.map((cat) => cat.displayString);

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(renderContext, catXLabels);
  }
  function renderRowLabels(renderContext: TpAreaRenderContext) {
    renderYAxisLabels(renderContext, selectedItemsMap, catYLabels);
  }

  function renderContingency(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    const unSelectedFill = interpolateColors(
      getStandardDataColor(),
      currentTheme.colors.background,
      0.3,
    ).toCanvasString();
    const selectedFill = getStandardDataColor().toCanvasString();

    ctx.fillStyle = getStandardDataColor().toCanvasString();
    ctx.strokeStyle = currentTheme.colors.text.toCanvasString();
    ctx.lineWidth = renderContext.pixelRatio * 1;

    ctx.strokeStyle = getThemedGrayFraction(0.9).toCanvasString();
    ctx.lineWidth = renderContext.pixelRatio * 0.5;
    for (let idxX = 0; idxX < catCountX; idxX++) {
      const xCent = mapx((idxX + 0.5) / catCountX);
      ctx.beginPath();
      ctx.moveTo(xCent, mapy(0));
      ctx.lineTo(xCent, mapy(1));
      ctx.stroke();
    }
    for (let idxY = 0; idxY < catCountY; idxY++) {
      const yCent = mapy((idxY + 0.5) / catCountY);
      ctx.beginPath();
      ctx.moveTo(mapx(0), yCent);
      ctx.lineTo(mapx(1), yCent);
      ctx.stroke();
    }

    const maxRadius = Math.min(
      (Math.abs(mapx(1) - mapx(0)) / catCountX) * 0.25,
      (Math.abs(mapy(1) - mapy(0)) / catCountY) * 0.25,
    );

    ctx.strokeStyle = getStandardDataColor().opacity(0.25).toCanvasString();
    ctx.lineWidth = renderContext.pixelRatio * 1;

    const gammaFactor = Math.exp(config.gamma / 2) / Math.exp(5 / 2);

    for (let idxX = 0; idxX < catCountX; idxX++) {
      const xCent = mapx((idxX + 0.5) / catCountX);
      for (let idxY = 0; idxY < catCountY; idxY++) {
        const isOtherCell = // true if the cell serves as an aggregator in case number of states was truncated
          categoriesXData[idxX].value == PLACEHOLDER_OTHER || categoriesYData[idxY].value == PLACEHOLDER_OTHER;
        const yCent = mapy((idxY + 0.5) / catCountY);

        if (!isOtherCell && filterInfo) {
          let isInFilter = false;
          for (const selectionCell of filterInfo.selectedCellsList)
            if (selectionCell.val1 == categoriesXData[idxX].value && selectionCell.val2 == categoriesYData[idxY].value)
              isInFilter = true;
          if (isInFilter) {
            ctx.fillStyle = getSelectionHighlightColor().opacity(0.35).toCanvasString();
            ctx.fillRect(
              mapx(idxX / catCountX),
              mapy(idxY / catCountY),
              mapx((idxX + 1) / catCountX) - mapx(idxX / catCountX),
              mapy((idxY + 1) / catCountY) - mapy(idxY / catCountY),
            );
          }
        }

        const count = cells[idxX][idxY].count;
        const countSelected = cells[idxX][idxY].countSelected;
        if (count > 0) {
          const rd = Math.sqrt(count / maxCellCount) ** gammaFactor * maxRadius;
          if (!isOtherCell) {
            ctx.beginPath();
            ctx.arc(xCent, yCent, pixelConv(rd), 0, 2 * Math.PI);
            if (countSelected == count) {
              ctx.fillStyle = selectedFill;
              ctx.fill();
            } else {
              ctx.fillStyle = unSelectedFill;
              ctx.fill();
              if (countSelected > 0) {
                ctx.beginPath();
                ctx.moveTo(xCent, yCent);
                ctx.arc(xCent, yCent, pixelConv(rd), 0, (countSelected / count) * 2 * Math.PI);
                ctx.closePath();
                ctx.fillStyle = selectedFill;
                ctx.fill();
              }
            }
          } else {
            ctx.beginPath();
            ctx.arc(xCent, yCent, pixelConv(rd), 0, 2 * Math.PI);
            ctx.stroke();
          }
        }
      }
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {}

  const hasLegend = false;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;

  const xAxisHeight = getLabelSpaceSize(config.labelSpaceX);
  const yAxisWidth = getLabelSpaceSize(config.labelSpaceY);

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const zpLogicalRangeY = createLogicalRange(1, 0);
  const zpRenderRangeX = createRenderRange(RenderMinDiffAbs(padding + yAxisWidth), RenderMaxDiffAbs(padding + 0));
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
  );

  const areaPoints: TpChartAreaDefinition = {
    id: 'contingency',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    // canAreaSelect: false,
    render: renderContingency,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaPoints,
    {
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
    {
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + yAxisWidth)), // offset of Y=4 to make axes overlap
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderRowLabels,
    },
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    },
  ];

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaPoints,
    dataTable,
    dataCatsX,
    dataCatsY,
    colCatsX,
    colCatsY,
    nameX,
    nameY,
    itemCount,
    dataItemId,
    contingencyTable,
    title,
  };
}
